import React, { useState, useMemo } from 'react';
import { Stack, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Button from '../../../../../../components/button';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { RequestFormFragment } from '../../../../__generated__/graphql';

const ALL_CATEGORIES = 'ALL';
const UNCATEGORIZED = 'UNCATEGORIZED';

type RequestFormSelectorProps = {
  requestForms: ReadonlyArray<
    Pick<
      RequestFormFragment,
      | 'Id'
      | 'Name'
      | 'flair__Information__c'
      | 'flair__Category__c'
      | 'categoryName'
    >
  >;
  selectedFormId: string | null;
  onFormSelect: (formId: string) => void;
};

export const RequestFormSelector: React.FC<
  React.PropsWithChildren<RequestFormSelectorProps>
> = ({ requestForms, selectedFormId, onFormSelect }) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] =
    useState<string>(ALL_CATEGORIES);

  const categories = useMemo(() => {
    const byId = new Map<string, string>();
    requestForms.forEach((form) => {
      if (form.flair__Category__c) {
        byId.set(
          form.flair__Category__c,
          form.categoryName ?? form.flair__Category__c,
        );
      }
    });
    return Array.from(byId, ([id, name]) => ({ id, name }));
  }, [requestForms]);

  const hasUncategorized = useMemo(
    () => requestForms.some((form) => !form.flair__Category__c),
    [requestForms],
  );

  const filteredForms = useMemo(() => {
    return requestForms.filter((form) => {
      const matchesSearch = form.Name.toLowerCase().includes(
        searchTerm.toLowerCase(),
      );
      const matchesCategory =
        selectedCategory === ALL_CATEGORIES ||
        (selectedCategory === UNCATEGORIZED && !form.flair__Category__c) ||
        form.flair__Category__c === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [requestForms, searchTerm, selectedCategory]);

  return (
    <>
      <div className="ps-3 mb-3 input-group-flush border rounded d-flex gap-3 input-group-merge">
        <InputGroup.Text className="d-flex align-items-center p-0">
          <FlairIcon icon="search-filled-new" />
        </InputGroup.Text>
        <input
          className="list-search form-control"
          type="search"
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          placeholder={t('requestForm.search')}
        />
      </div>

      {categories.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            label={t('requestForm.allCategories')}
            variant={
              selectedCategory === ALL_CATEGORIES
                ? 'primary'
                : 'outline-primary'
            }
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              setSelectedCategory(ALL_CATEGORIES);
            }}
          />
          {categories.map((category) => (
            <Button
              key={category.id}
              label={category.name}
              variant={
                selectedCategory === category.id ? 'primary' : 'outline-primary'
              }
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCategory(category.id);
              }}
            />
          ))}
          {hasUncategorized && (
            <Button
              label={t('requestForm.uncategorized')}
              variant={
                selectedCategory === UNCATEGORIZED
                  ? 'primary'
                  : 'outline-primary'
              }
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCategory(UNCATEGORIZED);
              }}
            />
          )}
        </div>
      )}

      <Stack gap={2}>
        {filteredForms.map((form) => (
          <OverlayTrigger
            key={form.Id}
            placement="right"
            overlay={
              <Tooltip id={`tooltip-${form.Id}`}>
                {form.flair__Information__c}
              </Tooltip>
            }>
            <div>
              <Button
                label={form.Name}
                variant={
                  selectedFormId === form.Id ? 'primary' : 'outline-primary'
                }
                className="text-start p-3 w-100"
                onClick={(e) => {
                  e.preventDefault();
                  onFormSelect(form.Id);
                }}
              />
            </div>
          </OverlayTrigger>
        ))}
      </Stack>
    </>
  );
};
